import { EXERCISES } from '@/data/exercises';

export interface ExerciseData {
  exercise_name: string;
  sets: number;
  reps: number;
  weight_kg?: number;
}

export interface SingleWorkoutPlan {
  type: 'single_workout';
  exercises: ExerciseData[];
}

export interface WeeklyWorkoutDay {
  day: string;
  workout_name: string;
  exercises: ExerciseData[];
}

export interface WeeklyWorkoutPlan {
  type: 'weekly_plan';
  days: WeeklyWorkoutDay[];
}

export interface RunningInterval {
  repeat?: number;
  distance_km?: number;
  duration_minutes?: number;
  pace?: string;
  rest_minutes?: number;
}

export interface RunningWorkoutPlan {
  type: 'running_workout';
  title?: string;
  warmup_minutes?: number;
  intervals: RunningInterval[];
  cooldown_minutes?: number;
  notes?: string;
}

export type WorkoutPlan = SingleWorkoutPlan | WeeklyWorkoutPlan | RunningWorkoutPlan;

// Function to find matching exercise from the database
export const findMatchingExercise = (exerciseName: string) => {
  const normalizedName = exerciseName.toLowerCase().trim();
  
  // First try exact match
  let exercise = EXERCISES.find(ex => ex.name.toLowerCase() === normalizedName);
  
  if (exercise) return exercise;
  
  // Try partial match
  exercise = EXERCISES.find(ex => 
    ex.name.toLowerCase().includes(normalizedName) || 
    normalizedName.includes(ex.name.toLowerCase())
  );
  
  if (exercise) return exercise;
  
  // Try alternative names and common variations
  const exerciseMap: { [key: string]: string } = {
    'pushup': 'Push-Up',
    'pushups': 'Push-Up',
    'push up': 'Push-Up',
    'pullup': 'Pull-Up',
    'pullups': 'Pull-Up',
    'pull up': 'Pull-Up',
    'situp': 'Sit-Up',
    'situps': 'Sit-Up',
    'sit up': 'Sit-Up',
    'bicep curl': 'Dumbbell Curl',
    'biceps curl': 'Dumbbell Curl',
    'shoulder press': 'Dumbbell Shoulder Press',
    'military press': 'Overhead Press',
    'chest fly': 'Dumbbell Chest Fly',
    'chest flye': 'Dumbbell Chest Fly',
    'lat pulldown': 'Lat Pulldown With Pronated Grip',
    'barbell row': 'Barbell Row',
    'bent over row': 'Barbell Row',
    'tricep extension': 'Dumbbell Standing Triceps Extension',
    'triceps extension': 'Dumbbell Standing Triceps Extension',
    'leg press': 'Leg Press',
    'calf raise': 'Standing Calf Raise',
    'leg curl': 'Lying Leg Curl',
    'leg extension': 'Leg Extension',
    'hip thrust': 'Hip Thrust',
    'glute bridge': 'Glute Bridge'
  };
  
  const mappedName = exerciseMap[normalizedName];
  if (mappedName) {
    exercise = EXERCISES.find(ex => ex.name === mappedName);
    if (exercise) return exercise;
  }
  
  return null;
};

// Function to parse JSON from Arnold's response
export const parseWorkoutPlanFromMessage = (message: string): WorkoutPlan | null => {
  try {
    // Prefer the LAST JSON code block in the message (most likely the actual plan)
    const blocks = Array.from(message.matchAll(/```json\s*([\s\S]*?)\s*```/g));
    let jsonString = '';

    if (blocks.length > 0) {
      jsonString = blocks[blocks.length - 1][1];
    } else {
      // Fallback: look for a JSON object without code fences
      const jsonMatch = message.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        jsonString = jsonMatch[0];
      }
    }

    if (!jsonString) return null;

    const parsed = JSON.parse(jsonString);

    // Helpers to coerce numbers safely from mixed formats (e.g., "4", "4x8", "8-10", "80 kg")
    // ALWAYS picks the HIGHEST value from ranges (e.g., "10-12" returns 12, "90-95kg" returns 95)
    const toInt = (val: any) => {
      if (val === null || val === undefined) return undefined;
      if (typeof val === 'number' && Number.isFinite(val)) return Math.round(val);
      if (typeof val === 'string') {
        // Extract ALL numbers from the string
        const matches = val.match(/-?\d+(?:\.\d+)?/g);
        if (!matches) return undefined;
        // Parse all numbers and return the MAXIMUM (for ranges like "10-12" or "90-95kg")
        const nums = matches.map(n => parseFloat(n.replace(',', '.'))).filter(n => !isNaN(n));
        if (nums.length === 0) return undefined;
        return Math.round(Math.max(...nums));
      }
      return undefined;
    };

    const toFloat = (val: any) => {
      if (val === null || val === undefined) return undefined;
      if (typeof val === 'number' && Number.isFinite(val)) return val;
      if (typeof val === 'string') {
        // Extract ALL numbers from the string
        const matches = val.replace(',', '.').match(/-?\d+(?:\.\d+)?/g);
        if (!matches) return undefined;
        // Parse all numbers and return the MAXIMUM (for ranges like "10-12" or "90-95kg")
        const nums = matches.map(n => parseFloat(n)).filter(n => !isNaN(n));
        if (nums.length === 0) return undefined;
        return Math.max(...nums);
      }
      return undefined;
    };

    // Validate the structure
    if (parsed.type === 'single_workout' && Array.isArray(parsed.exercises)) {
      // Normalize and validate exercises
      const normalized = parsed.exercises
        .map((ex: any) => {
          const rawWeight = ex.weight_kg ?? ex.weight ?? ex.weightKg;
          return {
            exercise_name: String(ex.exercise_name || '').trim(),
            sets: toInt(ex.sets),
            reps: toInt(ex.reps),
            weight_kg: rawWeight !== undefined ? toFloat(rawWeight) : undefined,
          };
        })
        .filter((ex: any) => ex.exercise_name && typeof ex.sets === 'number' && typeof ex.reps === 'number');

      if (normalized.length > 0) {
        return {
          type: 'single_workout',
          exercises: normalized,
        };
      }
    } else if (parsed.type === 'weekly_plan' && Array.isArray(parsed.days)) {
      // Normalize weekly plan
      const validDays = parsed.days
        .map((day: any) => ({
          day: String(day.day || '').trim(),
          workout_name: String(day.workout_name || '').trim(),
          exercises: Array.isArray(day.exercises)
            ? day.exercises
                .map((ex: any) => ({
                  exercise_name: String(ex.exercise_name || '').trim(),
                  sets: toInt(ex.sets),
                  reps: toInt(ex.reps),
                  weight_kg: ex.weight_kg !== undefined ? toFloat(ex.weight_kg) : (ex.weight !== undefined ? toFloat(ex.weight) : (ex.weightKg !== undefined ? toFloat(ex.weightKg) : undefined)),
                }))
                .filter((ex: any) => ex.exercise_name && typeof ex.sets === 'number' && typeof ex.reps === 'number')
            : [],
        }))
        .filter((day: any) => day.day && day.workout_name && day.exercises.length > 0);

      if (validDays.length > 0) {
        return {
          type: 'weekly_plan',
          days: validDays,
        };
      }
    } else if (parsed.type === 'running_workout') {
      const toIntSimple = (v: any) => {
        if (typeof v === 'number') return Math.round(v);
        if (typeof v === 'string') { const m = v.match(/-?\d+(?:\.\d+)?/g); if (!m) return undefined; return Math.round(Math.max(...m.map(n => parseFloat(n.replace(',', '.'))).filter(n => !isNaN(n)))); }
        return undefined;
      };
      const toFloatSimple = (v: any) => {
        if (typeof v === 'number') return v;
        if (typeof v === 'string') { const m = v.replace(',', '.').match(/-?\d+(?:\.\d+)?/g); if (!m) return undefined; return Math.max(...m.map(n => parseFloat(n)).filter(n => !isNaN(n))); }
        return undefined;
      };
      const intervals = Array.isArray(parsed.intervals) ? parsed.intervals.map((it: any) => ({
        repeat: toIntSimple(it.repeat) || 1,
        distance_km: toFloatSimple(it.distance_km),
        duration_minutes: toFloatSimple(it.duration_minutes),
        pace: typeof it.pace === 'string' ? it.pace : undefined,
        rest_minutes: toFloatSimple(it.rest_minutes),
      })) : [];
      return {
        type: 'running_workout',
        title: String(parsed.title || '').trim() || undefined,
        warmup_minutes: toIntSimple(parsed.warmup_minutes),
        intervals,
        cooldown_minutes: toIntSimple(parsed.cooldown_minutes),
        notes: typeof parsed.notes === 'string' ? parsed.notes : undefined,
      } as RunningWorkoutPlan;
    }

    return null;
  } catch (error) {
    console.error('Error parsing workout plan from message:', error);
    return null;
  }
};

// Function to convert parsed workout plan to workout logger format
export const convertToWorkoutLoggerFormat = (plan: WorkoutPlan) => {
  if (plan.type === 'single_workout') {
    return plan.exercises.map(exercise => {
      const matchedExercise = findMatchingExercise(exercise.exercise_name);
      return {
        id: `imported-${Date.now()}-${Math.random()}`,
        name: matchedExercise?.name || exercise.exercise_name,
        sets: exercise.sets,
        reps: exercise.reps,
        weight: exercise.weight_kg || 0,
        muscleGroups: matchedExercise?.muscle_groups || [],
        equipment: matchedExercise?.equipment,
        matched: !!matchedExercise
      };
    });
  }
  
  return null; // Weekly plans need different handling
};

// Function to import single workout into workout logger
export const importSingleWorkout = (
  plan: SingleWorkoutPlan,
  onImport: (exercises: any[]) => void
) => {
  const exercises = convertToWorkoutLoggerFormat(plan);
  if (exercises) {
    onImport(exercises);
    return true;
  }
  return false;
};